import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import { getBaseUrl, buildBreadcrumbSchema, buildWebPageSchema } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Mail, MessageCircle, ArrowRight } from "lucide-react";

import { FABCHAT_WHATSAPP_URL } from "@/lib/contact";

const CONTATO_EMAIL = "atendimento.fabtech@gmail.com";

const CONTATO_TITLE = "Contato | FabChat";
const CONTATO_DESCRIPTION =
  "Contato da FabChat por e-mail e WhatsApp para dúvidas sobre agente de IA para WhatsApp/Instagram, planos e suporte da plataforma.";

const breadcrumbItems = [
  { name: "Início", path: "/" },
  { name: "Contato", path: "/contato" },
];

type ContatoProps = {
  canonicalPath?: string;
};

export default function Contato({ canonicalPath = "/contato" }: ContatoProps) {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, breadcrumbItems);
  const webPageSchema = buildWebPageSchema(baseUrl, {
    path: canonicalPath,
    title: CONTATO_TITLE,
    description: CONTATO_DESCRIPTION,
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead
        title={CONTATO_TITLE}
        description={CONTATO_DESCRIPTION}
        canonicalPath={canonicalPath}
      />
      <StructuredData data={[breadcrumbSchema, webPageSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-2xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contato</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Escolha como prefere falar com a gente.
          </p>

          <div className="space-y-6 mb-12">
            <a
              href={`mailto:${CONTATO_EMAIL}`}
              className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Mail className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-lg">E-mail</h2>
                <p className="text-muted-foreground text-sm">Envie sua dúvida ou solicitação</p>
                <p className="text-primary font-medium mt-1">{CONTATO_EMAIL}</p>
              </div>
            </a>

            <a
              href={FABCHAT_WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <MessageCircle className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-lg">WhatsApp</h2>
                <p className="text-muted-foreground text-sm">Conversa direta pelo WhatsApp</p>
                <p className="text-primary font-medium mt-1">Abrir conversa</p>
              </div>
            </a>
          </div>

          <p className="text-sm text-muted-foreground mb-8">
            Responderemos o mais breve possível. Para informações sobre planos e preços, confira também a página de planos.
          </p>

          <section className="prose prose-slate dark:prose-invert max-w-none text-sm text-muted-foreground">
            <h2 className="text-lg font-semibold text-foreground">Sobre a empresa</h2>
            <p>
              A FabChat é operada pela FabTech (CNPJ 64.052.610/0001-11). O atendimento comercial e de suporte
              é realizado principalmente de forma remota, por e-mail e WhatsApp, para equipes em todo o Brasil.
              Integrações de API e acesso técnico para parceiros são tratados comercialmente com a equipe FabChat — use este contato ou o e-mail acima.
            </p>
          </section>

          <Button asChild size="lg" className="gap-2">
            <Link to="/planos">
              Ver planos <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </section>
      </main>

      <Footer />
    </div>
  );
}
